
import React from 'react';
import { Link } from '@/types/link-short';
import { Button } from '@/components/ui/button';
import { ExternalLink, Edit, Trash2, Copy } from 'lucide-react';
import { getIconComponent } from '@/components/link-short/IconUtils';

interface LinkCardProps {
  link: Link;
  isPreview?: boolean;
  onEdit?: (link: Link) => void;
  onDelete?: (linkId: string) => void;
  onClick?: (link: Link) => void;
  themeSettings?: {
    buttonStyle?: 'filled' | 'outline' | 'subtle' | 'glass' | 'gradient';
    buttonBgColor?: string;
    buttonTextColor?: string;
    borderRadius?: string;
    buttonAnimation?: 'none' | 'scale' | 'bounce' | 'glow';
    iconPosition?: 'left' | 'right' | 'none';
    iconSize?: 'small' | 'medium' | 'large';
  };
}

export function LinkCard({ 
  link, 
  isPreview = false, 
  onEdit, 
  onDelete, 
  onClick,
  themeSettings = {}
}: LinkCardProps) {
  const {
    buttonStyle = 'filled',
    buttonBgColor = '#ffffff',
    buttonTextColor = '#1e293b',
    borderRadius = '16px',
    buttonAnimation = 'scale',
    iconPosition = 'left',
    iconSize = 'medium'
  } = themeSettings;

  const handleClick = () => {
    if (onClick) {
      onClick(link);
    } else if (isPreview) {
      window.open(link.url, '_blank');
    }
  };

  const IconComponent = getIconComponent(link.icon);
  const iconSizeClass = {
    small: 'h-4 w-4',
    medium: 'h-5 w-5',
    large: 'h-6 w-6'
  }[iconSize];

  const animationClass = {
    none: '',
    scale: 'hover:scale-[1.02] active:scale-[0.98]',
    bounce: 'hover:animate-pulse',
    glow: 'hover:shadow-lg hover:shadow-blue-500/25'
  }[buttonAnimation];

  // Determinar estilo do link
  const linkStyle = link.link_style || 'default';
  
  const getButtonStyles = () => {
    const baseColor = link.bg_color || buttonBgColor;
    const textColor = link.text_color || buttonTextColor;
    
    const baseStyles = {
      borderRadius: borderRadius,
      transition: 'all 0.2s ease-in-out'
    };

    switch (linkStyle) {
      case 'pill':
        return {
          ...baseStyles,
          backgroundColor: baseColor,
          color: textColor,
          borderRadius: '50px',
          border: 'none',
          boxShadow: isPreview ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
        };
      
      case 'square':
        return {
          ...baseStyles,
          backgroundColor: baseColor,
          color: textColor,
          borderRadius: '8px',
          border: 'none',
          boxShadow: isPreview ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
        };
      
      case 'minimal':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: textColor,
          border: `2px solid ${baseColor}`,
          borderRadius: borderRadius
        };
      
      case 'gradient':
        return {
          ...baseStyles,
          background: `linear-gradient(135deg, ${baseColor} 0%, ${adjustBrightness(baseColor, -20)} 100%)`,
          color: textColor,
          border: 'none',
          borderRadius: borderRadius,
          boxShadow: isPreview ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
        };
      
      case 'glass':
        return {
          ...baseStyles,
          backgroundColor: `${baseColor}20`,
          color: textColor,
          border: `1px solid ${baseColor}40`,
          borderRadius: borderRadius,
          backdropFilter: 'blur(10px)',
          boxShadow: isPreview ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
        };
      
      default:
        return {
          ...baseStyles,
          backgroundColor: baseColor,
          color: textColor,
          border: 'none',
          borderRadius: borderRadius,
          boxShadow: isPreview ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
        };
    }
  };

  // Função auxiliar para ajustar brilho da cor
  const adjustBrightness = (color: string, percent: number) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  };

  return (
    <div className={`relative group ${animationClass} transition-all duration-200`}>
      <Button
        variant="ghost"
        className="w-full h-auto p-0 text-left font-medium relative overflow-hidden"
        style={getButtonStyles()}
        onClick={handleClick}
      >
        <div className="flex items-center justify-between p-4 w-full">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {IconComponent && iconPosition !== 'none' && iconPosition !== 'right' && (
              <div className="flex-shrink-0">
                <IconComponent className={iconSizeClass} />
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-base truncate mb-1">
                {link.title}
              </div>
              {isPreview && link.url && (
                <div className="text-xs opacity-70 truncate">
                  {link.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </div>
              )}
              {!isPreview && (
                <div className="text-xs opacity-60 truncate">
                  {link.clicks} cliques
                </div>
              )}
            </div>

            {IconComponent && iconPosition === 'right' && (
              <div className="flex-shrink-0">
                <IconComponent className={iconSizeClass} />
              </div>
            )}
            
            {isPreview && (
              <div className="flex-shrink-0">
                <ExternalLink className="h-4 w-4 opacity-50 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </div>
        </div>

        {/* Hover overlay effect */}
        {isPreview && (
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-[inherit]" />
        )}
      </Button>

      {/* Edit/Delete buttons for non-preview mode */}
      {!isPreview && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200">
          <div className="flex gap-1 bg-white rounded-lg shadow-lg p-1 border">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(link.url);
              }}
              title="Copiar link"
            >
              <Copy className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(link);
              }}
              title="Editar"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(link.id);
              }}
              title="Excluir"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
